/**
 * Safe Push Notification Bootstrap
 *
 * This module provides a safe way to initialize push notifications with:
 * - Idempotency (prevents double initialization)
 * - Permission check and request for Android 13+
 * - Proper listener registration before token request
 * - Comprehensive logging for debugging
 * - No side effects on module import
 */

import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { requestToken } from '../firebase'
import { handlePushMessage } from './handlePushMessage'
import { store } from '../services/store'
import { requestDashboard } from '../services/store/actions/dashboard/DashboardAction'
import DashboardActionTypes from '../services/store/actions/dashboard/DashboardActionTypes'

// Mirror the foreground dashboard-refresh side effects when a notification is
// opened from a background tap (the tap path does not go through onMessageListener).
const refreshDashboardOnTap = () => {
  const state = store.getState()
  if (!state.Auth.isLogged) return
  store.dispatch({ type: DashboardActionTypes.RESTART_INTERVAL, payload: null })
  store.dispatch(
    requestDashboard({
      date: state.Dashboard.selectedDate,
      shouldResetOpenCycles: false,
    })
  )
}

// Module-level flag to prevent double initialization
let isInitialized = false

/**
 * Initialize push notifications safely with idempotency guard
 *
 * Features:
 * - Registers listeners BEFORE requesting token/permissions
 * - Checks and requests Android 13+ POST_NOTIFICATIONS permission
 * - Prevents multiple initialization calls
 * - Comprehensive logging for debugging
 * - Returns early on web platform or if already initialized
 */
export async function initPush(): Promise<void> {
  // Idempotency guard - prevent double initialization
  if (isInitialized) {
    console.log('[Push] Already initialized, skipping')
    return
  }

  console.log('[Push] Starting push notification initialization')

  try {
    if (!Capacitor.isNativePlatform()) {
      console.log('[Push] Web platform - skipping native push setup')
      return
    }

    console.log('[Push] Native platform detected - setting up push notifications')

    // Step 1: Register core listeners BEFORE requesting permissions/token
    // Note: pushNotificationReceived is handled by NotificationRepository after login
    console.log('[Push] Registering core push notification listeners')

    PushNotifications.addListener('registration', (token) => {
      console.log('[Push] Registration successful, token:', token.value.substring(0, 50) + '...')
    })

    PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push] Registration failed:', error)
    })

    // Note: pushNotificationReceived listener is registered by NotificationRepository after login
    // to avoid duplicate listeners and ensure proper app state handling

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[Push] Notification action performed:', {
        actionId: action.actionId,
        notificationId: action.notification.id,
      })
      // Opening the app from a background tap also surfaces the localized in-app
      // banner. The tap intent carries the envelope under notification.data but no
      // server body, so bodyFallback is false and an unrenderable push is skipped.
      // No deep-link routing — background navigation is intentionally unchanged.
      const data = (action.notification.data ?? {}) as Record<string, string>
      handlePushMessage(
        {
          id: data.hash ?? action.notification.id ?? String(Date.now()),
          text: '',
          data,
        },
        { bodyFallback: false },
        refreshDashboardOnTap
      )
    })

    console.log('[Push] Core push notification listeners registered')

    // Step 2: Check current permission status
    const permissionStatus = await PushNotifications.checkPermissions()
    console.log('[Push] Permission status:', permissionStatus.receive)

    // Step 3: Request permission if needed (Android 13+ requires explicit permission)
    if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
      console.log('[Push] Requesting push notification permissions')

      const requestResult = await PushNotifications.requestPermissions()
      console.log('[Push] Permission request result:', requestResult.receive)

      if (requestResult.receive === 'denied') {
        console.warn('[Push] Permission denied by user')
        isInitialized = true // Still mark as initialized to prevent retries
        return
      }
    } else if (permissionStatus.receive === 'denied') {
      console.warn('[Push] Permission previously denied')
      isInitialized = true
      return
    }

    console.log('[Push] Permissions granted, proceeding with registration')

    // Step 4: Request token (this will trigger PushNotifications.register() internally)
    const token = await requestToken()

    if (token) {
      console.log('[Push] Token obtained successfully:', token.substring(0, 50) + '...')
    } else {
      console.warn('[Push] Failed to obtain token')
    }

    // Mark as successfully initialized
    isInitialized = true
    console.log('[Push] Push notification initialization completed successfully')

  } catch (error) {
    console.error('[Push] Error during push initialization:', error)
    // Still mark as initialized to prevent infinite retry loops
    isInitialized = true
    throw error
  }
}

/**
 * Reset the initialization state (for testing purposes)
 */
export function resetPushInit(): void {
  isInitialized = false
  console.log('[Push] Initialization state reset')
}

/**
 * Check if push notifications have been initialized
 */
export function isPushInitialized(): boolean {
  return isInitialized
}
